/**
 * Represents a USD currency value in cents (integer).
 */
export class USD {
  private cents: number;

  private constructor(cents: number) {
    this.cents = cents;
  }

  /**
   * Parses a string (e.g., "$12.34", "12.34") into a USD instance.
   */
  static parse(input: string): USD | null {
    const normalized = input.replace(/[^0-9.]/g, '');
    if (!normalized) return null;

    const [dollarsPart, centsPart = '0'] = normalized.split('.');
    // Handle cases like ".25" (no dollars part)
    const dollars = dollarsPart === '' ? '0' : dollarsPart;
    // If both dollars and cents are not numbers, return null
    if (isNaN(Number(dollars)) || isNaN(Number(centsPart))) return null;
    const totalCents =
      parseInt(dollars, 10) * 100 +
      parseInt((centsPart + '00').slice(0, 2), 10);
    return new USD(totalCents);
  }

  /**
   * Multiplies by a number.
   */
  multiply(factor: number): USD {
    return new USD(Math.floor(this.cents * factor));
  }

  /**
   * Formats the USD value as a string (e.g., "12.34").
   */
  format(): string {
    const absCents = Math.abs(this.cents);
    const dollars = Math.floor(absCents / 100);
    const cents = absCents % 100;
    return cents === 0
      ? `${dollars}`
      : `${dollars}.${cents.toString().padStart(2, '0')}`;
  }
}
